/**
 * Pure business rules for Professional Contacts / Professional Details (CLAUDE.md §5).
 *
 * No React, no Redux, no fetch — everything here is a plain function so it can be unit
 * tested in isolation and so components stay free of business logic (golden rule #3).
 */
import { getFormattedDate } from "../../Utils/Helpers/dateUtils";
import {
  LOAN_DETAIL_REQUIRED_ROLES,
  LOAN_DETAIL_ROLE_LABELS,
} from "./professionalContacts.types";

// ---------------------------------------------------------------------------------------
// Firm selectability / permissions
// ---------------------------------------------------------------------------------------

/**
 * A firm can be picked for a role on a loan only if it is active and not flagged
 * "no further instructions" — applies to every role, including broker/borrower's
 * solicitor/administrator (DF171-175).
 * @param {import("./professionalContacts.types").Firm|import("./professionalContacts.types").FirmSummary} firm
 * @returns {boolean}
 */
export function isSelectableOnLoan(firm) {
  if (!firm) return false;
  return Boolean(firm.active) && !firm.noFurtherInstructions;
}

/**
 * The "No further instructions" flag may only be changed by an Operational Supervisor
 * (DF176). The API is the backstop (403); this just drives whether the UI enables the toggle.
 * @param {{roles?: string[]}|null|undefined} user
 * @returns {boolean}
 */
export function canEditNoFurther(user) {
  return Boolean(
    user &&
      Array.isArray(user.roles) &&
      user.roles.includes("OperationalSupervisor")
  );
}

// ---------------------------------------------------------------------------------------
// PII status
// ---------------------------------------------------------------------------------------

/** Months-to-expiry that counts as "Expiring" rather than "Valid". TODO(confirm with Nicola, §7). */
export const PII_EXPIRING_WINDOW_MONTHS = 3;

/**
 * @param {import("./professionalContacts.types").Firm} firm
 * @param {number} [expiringWithinMonths]
 * @param {Date} [now] injectable for tests
 * @returns {"Valid"|"Expiring"|"None"}
 */
export function piiStatus(
  firm,
  expiringWithinMonths = PII_EXPIRING_WINDOW_MONTHS,
  now = new Date()
) {
  if (
    !firm ||
    firm.piiValue === undefined ||
    firm.piiValue === null ||
    !firm.piiExpiry
  ) {
    return "None";
  }
  const expiry = new Date(firm.piiExpiry);
  if (Number.isNaN(expiry.getTime())) return "None";
  const threshold = new Date(now);
  threshold.setMonth(threshold.getMonth() + expiringWithinMonths);
  if (expiry.getTime() < now.getTime()) return "Expiring"; // already lapsed reads as expiring, not silently valid
  return expiry.getTime() <= threshold.getTime() ? "Expiring" : "Valid";
}

// ---------------------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------------------

/** Max quotes per type (Valuer/Surveyor/Solicitor), enforced client-side in the quotes editor. TODO(confirm with Nicola, §7). */
export const MAX_QUOTES_PER_TYPE = 3;

/**
 * @param {import("./professionalContacts.types").Quote[]|undefined} quotes full quotes array
 * @param {string} type one of QUOTE_TYPES
 * @returns {import("./professionalContacts.types").Quote[]}
 */
export function quotesForType(quotes, type) {
  return Array.isArray(quotes)
    ? quotes.filter((q) => q && q.role === type)
    : [];
}

/**
 * @param {import("./professionalContacts.types").Quote[]|undefined} quotes
 * @param {string} type
 * @returns {boolean}
 */
export function canAddQuote(quotes, type) {
  return quotesForType(quotes, type).length < MAX_QUOTES_PER_TYPE;
}

// ---------------------------------------------------------------------------------------
// Quote supporting documents
// ---------------------------------------------------------------------------------------

/**
 * The Loan Documents category every quote's supporting document is filed under
 * (DF.TblFileCategory, IsSystemGenerated = 1) — fixed, never user-editable from the quotes
 * editor. See docs/planning/professional-contacts-quote-documents/00-overview.md.
 */
export const QUOTE_DOCUMENT_CATEGORY_NAME = "Quotes";

/**
 * File extensions accepted for a quote's supporting document — the business-specified subset
 * (PDF, Word, Excel, images). Kept as its own constant rather than importing the app-wide
 * ALLOWED_FILE_EXTENSIONS (M_AddTaskForm.helper.js) directly: that list also allows
 * .msg/.eml/.ppt/.pptx which this rule doesn't call for, and a domain module shouldn't depend
 * on a UI molecule's helper file. Keep the two lists in sync if either changes.
 */
export const QUOTE_DOCUMENT_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".jpg",
  ".jpeg",
  ".png",
  ".svg",
];

/**
 * Matches the app-wide MAX_FILE_SIZE_BYTES (M_AddTaskForm.helper.js) — no separate limit was
 * requested for quote documents, so reuse the same 10 MB ceiling rather than inventing a new one.
 */
export const QUOTE_DOCUMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * @param {string} fileName
 * @returns {boolean}
 */
export function isAllowedQuoteDocumentExtension(fileName) {
  if (!fileName || fileName.lastIndexOf(".") <= 0) return false;
  const ext = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
  return QUOTE_DOCUMENT_ALLOWED_EXTENSIONS.includes(ext);
}

/**
 * @param {{size?: number}|null|undefined} file a browser File
 * @returns {boolean}
 */
export function isQuoteDocumentTooLarge(file) {
  return Boolean(file && file.size > QUOTE_DOCUMENT_MAX_SIZE_BYTES);
}

/**
 * The Document Helper storage reference for a quote document —
 * "<loanId>/<QUOTE_DOCUMENT_CATEGORY_NAME>/<fileName>" — computed client-side the same way
 * DocumentsDashboard.jsx already does for every other Loan Document (it doesn't rely on the
 * Document Helper's upload response for the path either). The storage account and parent
 * container are resolved server-side from the `type`/`subdomain` headers, not part of this path.
 * @param {string} loanId
 * @param {string} fileName
 * @returns {string}
 */
export function buildQuoteDocumentFilePath(loanId, fileName) {
  return `${loanId}/${QUOTE_DOCUMENT_CATEGORY_NAME}/${fileName}`;
}

// ---------------------------------------------------------------------------------------
// Firm documents (v1.9.0)
//
// Deliberately a SIBLING of the QUOTE_DOCUMENT_* block above, not a generalisation of it — the
// two allow-lists are free to diverge (LLD §3.1). Do not refactor the two to share code.
// ---------------------------------------------------------------------------------------

/**
 * The literal first path segment of every firm document's blob key — see
 * buildFirmDocumentFilePath. Keeps a firm's documents in their own namespace so they can never
 * collide with, or surface inside, a loan's Documents Dashboard (loan documents are keyed
 * "{crmId}/{category}/{fileName}").
 */
export const FIRM_DOCUMENT_PATH_PREFIX = "professional-contacts";

/**
 * File extensions accepted for a firm document — same business-specified subset as
 * QUOTE_DOCUMENT_ALLOWED_EXTENSIONS today, but this is its own constant on purpose: the two
 * features' allow-lists have no contractual reason to stay identical, and a domain module
 * shouldn't depend on a UI molecule's helper file either. Keep in sync with the API's
 * AllowedFirmDocumentExtensions (ProfessionalFirmService.cs) if either changes.
 */
export const FIRM_DOCUMENT_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".jpg",
  ".jpeg",
  ".png",
  ".svg",
];

/**
 * Matches the app-wide MAX_FILE_SIZE_BYTES (M_AddTaskForm.helper.js) — no separate limit was
 * requested for firm documents, so reuse the same 10 MB ceiling rather than inventing a new one.
 * Q6 (HLD §9) may change this constant once confirmed for scanned certificates.
 */
export const FIRM_DOCUMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * @param {string} fileName
 * @returns {boolean}
 */
export function isAllowedFirmDocumentExtension(fileName) {
  if (!fileName || fileName.lastIndexOf(".") <= 0) return false;
  const ext = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
  return FIRM_DOCUMENT_ALLOWED_EXTENSIONS.includes(ext);
}

/**
 * @param {{size?: number}|null|undefined} file a browser File
 * @returns {boolean}
 */
export function isFirmDocumentTooLarge(file) {
  return Boolean(file && file.size > FIRM_DOCUMENT_MAX_SIZE_BYTES);
}

/**
 * Path-traversal guard for a firm document's file name (LLD §2.3 rule 2, mirrored client-side so
 * the dialog can reject before any network call, R7): must be ≤255 chars, contain a "." that
 * isn't the first character, and contain none of "/", "\", ".." or control characters — the file
 * name becomes a blob path segment. Matches ProfessionalFirmService.IsValidFirmDocumentFileName.
 * @param {string} name
 * @returns {boolean}
 */
export function isValidFirmDocumentFileName(name) {
  if (!name || name.length > 255) return false;
  if (name.includes("/") || name.includes("\\") || name.includes("..")) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(name)) return false;
  const dotIndex = name.indexOf(".");
  return dotIndex > 0;
}

/**
 * The Document Helper storage reference for a firm document —
 * "{FIRM_DOCUMENT_PATH_PREFIX}/{firmId}/{documentId}/{fileName}". This is the ONE string both
 * this UI and the API's ProfessionalFirmService.BuildFirmDocumentFilePath must agree on
 * byte-for-byte — the API server-recomputes and compares it on create (LLD §2.3 rule 4, §5
 * bullet 1), so if the format ever changes, both sides must change together or every upload
 * 400s. Cross-referenced by professionalContacts.rules.test.js and
 * ProfessionalFirmServiceDocumentValidationTests.cs, which pin the same literal.
 * Lower-cases firmId/documentId (mirroring the API's ToLowerInvariant()) so the two sides agree
 * byte-for-byte even if either GUID were ever rendered in a different case.
 * @param {string} firmId
 * @param {string} documentId
 * @param {string} fileName
 * @returns {string}
 */
export function buildFirmDocumentFilePath(firmId, documentId, fileName) {
  return `${FIRM_DOCUMENT_PATH_PREFIX}/${String(firmId).toLowerCase()}/${String(documentId).toLowerCase()}/${fileName}`;
}

// ---------------------------------------------------------------------------------------
// Contacts / addresses — completeness, defaults, delete guards
// ---------------------------------------------------------------------------------------

/**
 * Per the contract, a Contact is only "complete" once name+email+phone are all present —
 * partially-filled rows are neither valid additions nor silently dropped, they block save.
 * @param {import("./professionalContacts.types").Contact|null|undefined} contact
 * @returns {boolean}
 */
export function isCompleteContact(contact) {
  return Boolean(
    contact &&
      contact.name?.trim() &&
      contact.email?.trim() &&
      contact.phone?.trim()
  );
}

/** @param {import("./professionalContacts.types").Contact|null|undefined} contact */
export function isStartedContact(contact) {
  return Boolean(
    contact &&
      (contact.name?.trim() || contact.email?.trim() || contact.phone?.trim())
  );
}

/**
 * @param {import("./professionalContacts.types").Address|null|undefined} address
 * @returns {boolean}
 */
export function isCompleteAddress(address) {
  return Boolean(
    address &&
      address.building?.trim() &&
      address.street?.trim() &&
      address.town?.trim() &&
      address.postcode?.trim() &&
      address.country?.trim()
  );
}

/** @param {import("./professionalContacts.types").Address|null|undefined} address */
export function isStartedAddress(address) {
  return Boolean(
    address &&
      (address.building?.trim() ||
        address.street?.trim() ||
        address.town?.trim() ||
        address.postcode?.trim())
  );
}

/**
 * The firm's main contact, falling back to the first contact if none is flagged main.
 * Used to default the "Active individual" selector when a firm is picked on a loan.
 * @param {{contacts?: import("./professionalContacts.types").Contact[]}|null|undefined} firm
 * @returns {import("./professionalContacts.types").Contact|null}
 */
export function getMainContact(firm) {
  const contacts = firm?.contacts || [];
  return contacts.find((c) => c.isMain) || contacts[0] || null;
}

/** @param {{contacts?: import("./professionalContacts.types").Contact[]}|null|undefined} firm */
export function defaultContactId(firm) {
  return getMainContact(firm)?.id ?? null;
}

/**
 * The firm's preferred address, falling back to the first address if none is flagged preferred.
 * @param {{addresses?: import("./professionalContacts.types").Address[]}|null|undefined} firm
 * @returns {import("./professionalContacts.types").Address|null}
 */
export function getPreferredAddress(firm) {
  const addresses = firm?.addresses || [];
  return addresses.find((a) => a.isPreferred) || addresses[0] || null;
}

/** @param {{addresses?: import("./professionalContacts.types").Address[]}|null|undefined} firm */
export function defaultAddressId(firm) {
  return getPreferredAddress(firm)?.id ?? null;
}

/** A firm must keep at least one individual — the API's 400 is the backstop, this drives the UI. */
export function canDeleteContact(firm) {
  return Boolean(
    firm && Array.isArray(firm.contacts) && firm.contacts.length > 1
  );
}

/** A firm must keep at least one address — the API's 400 is the backstop, this drives the UI. */
export function canDeleteAddress(firm) {
  return Boolean(
    firm && Array.isArray(firm.addresses) && firm.addresses.length > 1
  );
}

// ---------------------------------------------------------------------------------------
// Bank accounts — completeness, defaults, delete guard (mirrors contacts/addresses, §3a of
// docs/planning/professional-contacts-third-party-payments) EXCEPT a firm may have ZERO bank
// accounts (confirmed decision) — unlike Contact/Address there is no keep-at-least-one guard.
// ---------------------------------------------------------------------------------------

export const SORT_CODE_REGEX = /^\d{2}-\d{2}-\d{2}$/;

export const SORT_CODE_FORMAT_ERROR = "Please enter the Sort Code in the format 12-34-56";

const SORT_CODE_SUMMARY_ERROR = "sort code must be in the format 12-34-56";

export function isValidSortCode(sortCode) {
  return SORT_CODE_REGEX.test((sortCode || "").trim());
}

/**
 * A bank account is only "complete" once Sort Code and the creditor bank name/town/postcode/
 * country/address are all present — Beneficiary Name, Bank Type and Account Number stay
 * optional (confirmed field list). Partially-filled rows block save rather than being silently
 * dropped, same as isCompleteContact/isCompleteAddress.
 * @param {import("./professionalContacts.types").BankAccount|null|undefined} bankAccount
 * @returns {boolean}
 */
export function isCompleteBankAccount(bankAccount) {
  return Boolean(
    bankAccount &&
      bankAccount.sortCode?.trim() &&
      bankAccount.bankName?.trim() &&
      bankAccount.town?.trim() &&
      bankAccount.postcode?.trim() &&
      bankAccount.country?.trim() &&
      bankAccount.address?.trim()
  );
}

/** @param {import("./professionalContacts.types").BankAccount|null|undefined} bankAccount */
export function isStartedBankAccount(bankAccount) {
  return Boolean(
    bankAccount &&
      (bankAccount.beneficiaryName?.trim() ||
        bankAccount.accountNumber?.trim() ||
        bankAccount.sortCode?.trim() ||
        bankAccount.bankName?.trim() ||
        bankAccount.town?.trim() ||
        bankAccount.postcode?.trim() ||
        bankAccount.country?.trim() ||
        bankAccount.address?.trim())
  );
}

/**
 * The firm's main bank account, falling back to the first if none is flagged main. Used to
 * default the Outbound Payments lock-fill and to render "Main account" on the firm record.
 * @param {{bankAccounts?: import("./professionalContacts.types").BankAccount[]}|null|undefined} firm
 * @returns {import("./professionalContacts.types").BankAccount|null}
 */
export function getMainBankAccount(firm) {
  const bankAccounts = firm?.bankAccounts || [];
  return bankAccounts.find((a) => a.isMain) || bankAccounts[0] || null;
}

/** @param {{bankAccounts?: import("./professionalContacts.types").BankAccount[]}|null|undefined} firm */
export function defaultBankAccountId(firm) {
  return getMainBankAccount(firm)?.id ?? null;
}

/**
 * Unlike canDeleteContact/canDeleteAddress, a firm may have zero bank accounts (confirmed
 * decision — the field list never marked bank accounts as minItems:1). Delete is never
 * blocked client-side; kept as a named function for symmetry with the contact/address guards
 * and so a future reversal of the decision has one place to change.
 * @returns {boolean}
 */
export function canDeleteBankAccount() {
  return true;
}

// ---------------------------------------------------------------------------------------
// Duplicate company names (FIRM-DUPLICATE-NAMES, contract v1.13.0)
// ---------------------------------------------------------------------------------------

/**
 * R1 — THE JS definition of when two company NAMES are "the same": trimmed, then compared
 * case-insensitively. Exact after trimming; deliberately NOT fuzzy (no stripping of
 * Ltd/Limited/LLP or punctuation), because that produces false positives between genuinely
 * distinct firms and there is no UI in this feature for overriding one.
 *
 * As of v1.14.0 (FIRM-DUPLICATE-NAMES-010/-012, R12) a matching NAME is no longer sufficient
 * on its own — two firms are only "the same" when they also share a firm type. This function
 * still compares names only (BulkUpload.workbook.js's in-file check and this form both still
 * need that on its own); see isSameFirmIdentity below for the full R1 rule.
 *
 * The same rule lives in two other languages and all three must agree:
 *   SQL — LTRIM(RTRIM(Name)) under the database's case-insensitive collation, AND
 *         FirmTypeCode = @FirmTypeCode (pc.usp_ProfessionalFirm_FindByName)
 *   C#  — ProfessionalFirmService.NormalizeFirmName + FirmNameComparer, scoped by firmTypeCode
 *
 * This is a convenience for warning the user early. The server is the authority: create and
 * update return 409 duplicate_name regardless of what the UI concluded.
 *
 * @param {unknown} name
 * @returns {string} "" for null/undefined/blank — never throws, never returns null
 */
export function normaliseFirmName(name) {
  return String(name ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Are these two company names the same company, per R1's NAME half?
 *
 * Two BLANK names are NOT "the same" — a missing company name is a validation error
 * (validateFirmDraft reports it), not a duplicate, and reporting it as a duplicate would put
 * the wrong message on the field.
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function isSameFirmName(a, b) {
  const left = normaliseFirmName(a);
  const right = normaliseFirmName(b);
  return left !== "" && left === right;
}

/**
 * v1.14.0 (FIRM-DUPLICATE-NAMES-010/-012, R12) — are these two FirmType WIRE values the same
 * type? Trimmed, case-insensitive, mirroring isSameFirmName's shape. Two blank types are NOT
 * "the same", for the same reason two blank names aren't: a missing type is a validation
 * problem the form must surface on its own, not something that should read as a match.
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function isSameFirmType(a, b) {
  const left = String(a ?? "")
    .trim()
    .toLowerCase();
  const right = String(b ?? "")
    .trim()
    .toLowerCase();
  return left !== "" && left === right;
}

/**
 * v1.14.0 (FIRM-DUPLICATE-NAMES-010/-012, R1/R12) — THE full JS rule: two firms are "the same"
 * only when their names AND their types both match. This is what FirmForm's duplicate warning
 * must key its comparisons on, not isSameFirmName alone — a warning raised for a Broker must
 * not keep standing once the user switches Type to Valuer.
 * @param {{name?: unknown, type?: unknown}|null|undefined} a
 * @param {{name?: unknown, type?: unknown}|null|undefined} b
 * @returns {boolean}
 */
export function isSameFirmIdentity(a, b) {
  return isSameFirmName(a?.name, b?.name) && isSameFirmType(a?.type, b?.type);
}

// ---------------------------------------------------------------------------------------
// Add / edit company validation (single summary, per §4a)
// ---------------------------------------------------------------------------------------

/**
 * @param {import("./professionalContacts.types").FirmUpsert} draft
 * @returns {boolean}
 */
export function isCompanyValid(draft) {
  return validateFirmDraft(draft).valid;
}

/**
 * Validates a firm add/edit draft and returns every missing/incomplete item so the UI can
 * render a single validation summary (§4a), instead of scattering field-level checks in JSX.
 * @param {import("./professionalContacts.types").FirmUpsert} draft
 * @returns {{valid: boolean, missing: string[]}}
 */
export function validateFirmDraft(draft) {
  const missing = [];
  const contacts = draft?.contacts || [];
  const addresses = draft?.addresses || [];
  const bankAccounts = draft?.bankAccounts || [];

  if (!draft?.name?.trim()) missing.push("Company name");
  if (!draft?.mainPhone?.trim()) missing.push("Main contact number");
  if (!draft?.email?.trim()) missing.push("Company email address");

  contacts.forEach((contact, index) => {
    if (isStartedContact(contact) && !isCompleteContact(contact)) {
      missing.push(
        `Individual ${index + 1}: name, email & phone are all required`
      );
    }
  });
  if (!contacts.some(isCompleteContact)) {
    missing.push("At least one complete individual (name, email & phone)");
  }

  addresses.forEach((address, index) => {
    if (isStartedAddress(address) && !isCompleteAddress(address)) {
      missing.push(
        `Address ${
          index + 1
        }: building, street, town, postcode & country are all required`
      );
    }
  });
  if (!addresses.some(isCompleteAddress)) {
    missing.push("At least one complete address");
  }

  // Bank accounts: a started-but-incomplete row still blocks save (same rule as
  // contacts/addresses), but — per the confirmed decision — there is no "at least one
  // complete bank account" requirement; a firm may legitimately have zero.
  bankAccounts.forEach((bankAccount, index) => {
    if (
      isStartedBankAccount(bankAccount) &&
      !isCompleteBankAccount(bankAccount)
    ) {
      missing.push(
        `Bank account ${
          index + 1
        }: sort code, creditor bank name, town, postcode, country & address are all required`
      );
    }
    if (bankAccount?.sortCode?.trim() && !isValidSortCode(bankAccount.sortCode)) {
      missing.push(`Bank account ${index + 1}: ${SORT_CODE_SUMMARY_ERROR}`);
    }
  });

  return { valid: missing.length === 0, missing };
}

// ---------------------------------------------------------------------------------------
// Professional Details editor validation (§4b) — which roles are mandatory is an open item
// (§7, "confirm with Nicola"); LOAN_DETAIL_REQUIRED_ROLES documents today's best guess from
// the client layout (bank's solicitor/valuer + borrower's solicitor carry an asterisk).
// ---------------------------------------------------------------------------------------

/**
 * @param {import("./professionalContacts.types").LoanProfessionalDetails} details
 * @returns {{valid: boolean, missing: string[]}}
 */
export function validateLoanDetailsDraft(details) {
  const missing = LOAN_DETAIL_REQUIRED_ROLES.filter(
    (roleKey) => !details?.[roleKey]?.firmId
  ).map((roleKey) => LOAN_DETAIL_ROLE_LABELS[roleKey]);
  return { valid: missing.length === 0, missing };
}

// ---------------------------------------------------------------------------------------
// Dates — display as dd/mm/yyyy (UK), transport/store as ISO YYYY-MM-DD (golden rule #5)
// ---------------------------------------------------------------------------------------

/**
 * Formats an ISO date (or any Date-parseable value) for display, reusing the app's existing
 * date formatter/locale. Never returns the raw ISO string — callers must not roll their own.
 * @param {string|Date|null|undefined} isoDate
 * @returns {string} "dd/mm/yyyy", or "" when there is no date
 */
export function toDisplayDate(isoDate) {
  if (!isoDate) return "";
  return getFormattedDate(isoDate, "DD/MM/YYYY");
}

/**
 * Normalises a Date (as produced by A_DatePicker et al.) to the ISO YYYY-MM-DD the contract
 * transports. Never send a dd/mm/yyyy string on the wire.
 * @param {Date|string|null|undefined} value
 * @returns {string|null}
 */
export function toIsoDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
